package dev.terrakok.mapitan.domain.utils

import androidx.compose.ui.graphics.Color
import kotlin.math.abs

/**
 * Converts HSV to [Color].
 *
 * @param h Hue in degrees [0, 360).
 * @param s Saturation [0, 1].
 * @param v Value [0, 1].
 */
fun hsvToColor(h: Float, s: Float, v: Float): Color {
    val c = v * s
    val x = c * (1 - abs((h / 60) % 2 - 1))
    val m = v - c

    val (r1, g1, b1) = when {
        h < 60 -> Triple(c, x, 0f)
        h < 120 -> Triple(x, c, 0f)
        h < 180 -> Triple(0f, c, x)
        h < 240 -> Triple(0f, x, c)
        h < 300 -> Triple(x, 0f, c)
        else -> Triple(c, 0f, x)
    }

    return Color(r1 + m, g1 + m, b1 + m, 1f)
}

/**
 * Converts [Color] to HSV.
 *
 * @return Triple of (hue [0, 360), saturation [0, 1], value [0, 1]).
 */
fun Color.toHSV(): Triple<Float, Float, Float> {
    val max = maxOf(red, green, blue)
    val min = minOf(red, green, blue)
    val delta = max - min

    val hue = when {
        delta == 0f -> 0f
        max == red -> 60f * (((green - blue) / delta) % 6)
        max == green -> 60f * ((blue - red) / delta + 2)
        else -> 60f * ((red - green) / delta + 4)
    }.let { if (it < 0) it + 360f else it }

    val saturation = if (max == 0f) 0f else delta / max
    val value = max

    return Triple(hue, saturation, value)
}

/**
 * Converts this [Color] to a hex string in the format `#RRGGBB`.
 */
fun Color.toHexRgb(): String {
    fun Int.toHexTwoDigits(): String {
        return if (this < 16) "0${toString(16).uppercase()}" else toString(16).uppercase()
    }

    val r = (this.red * 255).toInt()
    val g = (this.green * 255).toInt()
    val b = (this.blue * 255).toInt()
    return "#${r.toHexTwoDigits()}${g.toHexTwoDigits()}${b.toHexTwoDigits()}"
}

/**
 * Parses a `#RRGGBB` or `RRGGBB` hex string, returns `null` if the string is not a valid color.
 */
fun String.hexRgbToColorOrNull(): Color? {
    val cleaned = trim().removePrefix("#")
    if (cleaned.length != 6) return null
    val value = cleaned.toLongOrNull(16) ?: return null
    return Color(
        red = ((value shr 16) and 0xFF) / 255f,
        green = ((value shr 8) and 0xFF) / 255f,
        blue = (value and 0xFF) / 255f,
        alpha = 1f,
    )
}

fun getContrastColor(bg: Color): Color {
    val luminance = 0.2126 * bg.red + 0.7152 * bg.green + 0.0722 * bg.blue
    return if (luminance > 0.5) Color.Black else Color.White
}
